import { ArrowDownTrayIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { SendEmailDialog } from 'components/SendEmailDialog'
import { COMPANY_NAME_FC, servicingEmail } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  downloadServicingDocs2,
  getLoanContacts,
  getServicingDocumentData,
  sendServicingDocumentOnEmail,
} from 'services'
import { borrowerEmailParties, emailDirDefaults } from 'utils/borrowerRecipients'

import { ILoanContact } from '../Contacts'

// Docgen templates are .docx underneath and the PDF is a conversion of them, so they can come back
// as either. The TMO reports in the same list are drawn straight to PDF and have no Word form, so
// they keep the single plain Download action.
const DOCGEN_SOURCE = 'docgen'

interface ServicingDoc {
  name: string
  placeholders: string[]
  email: { subject: string; comment: string }
  source: string
}

// const sourceMap: Record<string, string> = {
//   docgen: 'Docgen',
//   tmo: 'TMO',
// }

export default function ServicingDocument() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const { borrower } = useSelector((state: any) => ({
    borrower: state.borrower.borrower,
  }))
  const [loading, setLoading] = useState(true)
  const [documents, setDocuments] = useState<ServicingDoc[]>([])
  const [placeholders, setPlaceholders] = useState<any>({})
  const [docRlt, setDocRlt] = useState<any>({})
  const [currentDocument, setCurrentDocument] = useState<ServicingDoc | null>(null)
  const [missingPlaceholders, setMissingPlaceholders] = useState<any>([])
  const [contacts, setContacts] = useState<ILoanContact[]>([])

  /**
   * Every address the borrower keeps, offered as its own row and all addressed by default.
   *
   * The Payoff screen reads the same source for the same reason: a borrower who lists additional
   * addresses expects the document at all of them, and offering only the primary meant the rest
   * were quietly left off every servicing document sent from here.
   */
  const borrowerParties = useMemo(() => borrowerEmailParties(borrower), [borrower])
  const borrowerDefaults = useMemo(
    () => emailDirDefaults(borrowerParties.map((row) => row.email)),
    [borrowerParties],
  )

  const applyDocumentData = (res: any) => {
    if (res?.success) {
      setDocuments(res.documents)
      setPlaceholders(res.placeholders)
      setDocRlt(res.docRlt)
    }
  }

  const init = async () => {
    setLoading(true)

    if (isShared) {
      applyDocumentData(shared.pageData)
      setLoading(false)
      return
    }

    const [res, contactsRes] = await Promise.all([getServicingDocumentData(), getLoanContacts({ isLoan: true })])
    applyDocumentData(res)
    setContacts(contactsRes.results)
    setLoading(false)
  }

  useEffect(() => {
    if (isShared) return
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        init()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isShared])

  useEffect(() => {
    init()
  }, [isShared, shared?.pageKey, shared?.pageData])

  const checkPlaceholders = (doc: ServicingDoc) => {
    let errors: any = []
    doc.placeholders.map((key) => {
      if (docRlt[key]?.length > 0) {
      } else {
        errors.push(placeholders[key] || key)
      }
    })
    errors = errors.sort()
    setMissingPlaceholders(errors)
    return true
  }

  const onDownload = (doc: ServicingDoc, format: 'pdf' | 'docx' = 'pdf') => {
    if (isShared) return
    setLoading(true)
    downloadServicingDocs2(doc, format).finally(() => setLoading(false))
  }

  const onSendToBorrower = (doc: ServicingDoc) => {
    if (isShared) return
    if (!checkPlaceholders(doc)) return
    setCurrentDocument(doc)
  }

  // A send that fails still answers 200 with `success: false`, and the response carries `status`
  // rather than `message`, so the Api layer's own toast stays quiet. Reporting success on any
  // resolved response told staff the document had gone out when nothing had been delivered.
  const onSendEmail = (emailData: Record<string, string>, data: Record<string, any>) => {
    if (!currentDocument) return
    return sendServicingDocumentOnEmail(currentDocument, emailData, data).then((res: any) => {
      if (res?.success === false) {
        toast(res?.status || 'The document could not be emailed.', { type: 'error' })
        return
      }
      toast('Document is sent on email successfully.', { type: 'info' })
    })
  }

  return (
    <div className="servicing-document-container">
      <div className="relative max-w-screen-2xl">
        <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
          <span>Servicing Documents</span>
        </h2>
        <LayoutLoading show={loading} />
        <div className="overflow-auto mb-6">
          <table className="text-[15px] w-full">
            <thead>
              <tr>
                <th className="border p-2">No</th>
                {/* <th className="border p-2">Source</th> */}
                <th className="border p-2">Document Name</th>
                {!isShared && <th className="border p-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {documents.map((item, index) => {
                return (
                  <tr key={index} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                    <td className="border p-2">{index + 1}</td>
                    <td className="border p-2">{item.name}</td>
                    {!isShared && (
                      <td className="border px-2 py-3">
                        <div className="flex flex-wrap gap-4">
                          {item.source === DOCGEN_SOURCE ? (
                            <>
                              <div
                                className="flex items-center gap-1 cursor-pointer hover:underline hover:text-shade-blue"
                                onClick={() => onDownload(item, 'pdf')}
                              >
                                Download PDF
                                <ArrowDownTrayIcon className="w-4 h-3"></ArrowDownTrayIcon>
                              </div>
                              <div
                                className="flex items-center gap-1 cursor-pointer hover:underline hover:text-shade-blue"
                                onClick={() => onDownload(item, 'docx')}
                              >
                                Download Word
                                <ArrowDownTrayIcon className="w-4 h-3"></ArrowDownTrayIcon>
                              </div>
                            </>
                          ) : (
                            <div
                              className="flex items-center gap-1 cursor-pointer hover:underline hover:text-shade-blue"
                              onClick={() => onDownload(item)}
                            >
                              Download
                              <ArrowDownTrayIcon className="w-4 h-3"></ArrowDownTrayIcon>
                            </div>
                          )}
                          <div
                            className="flex items-center gap-1 cursor-pointer hover:underline hover:text-shade-blue"
                            onClick={() => onSendToBorrower(item)}
                          >
                            Send to Borrower
                            <PaperAirplaneIcon className="w-4 h-3"></PaperAirplaneIcon>
                          </div>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {isShared && (
          <p className="text-sm text-gray-500 mb-4">
            Document download and email are not available in the shared read-only view.
          </p>
        )}
        {currentDocument && !isShared && (
          <SendEmailDialog
            title={`Send "${currentDocument.name}" Document`}
            data={[
              ...borrowerParties,
              { party: `${COMPANY_NAME_FC} Servicing`, email: servicingEmail() },
              ...contacts.map((contact) => ({ party: contact.name, email: contact.email })),
            ]}
            emailData={currentDocument.email}
            defaultValues={{
              ...borrowerDefaults,
              [servicingEmail()]: 'cc',
            }}
            onSubmit={onSendEmail}
            onClose={() => setCurrentDocument(null)}
            errors={missingPlaceholders}
            modalId="sendEmailServicingDocument"
          />
        )}
      </div>
    </div>
  )
}
